// engagement-components/CampaignForm.jsx
import React, { useState, useEffect } from "react";
import {
  Row, Col, Card, Form, Button, Alert, Spinner, Badge, Tabs, Tab,
} from "react-bootstrap";
import {
  FaArrowLeft, FaRocket, FaCalendarAlt, FaUsers, FaRupeeSign,
  FaEye, FaWhatsapp, FaSms, FaEnvelope, FaArrowRight, FaUserEdit, FaPaperPlane,
} from "react-icons/fa";
import axios from "axios";
import dayjs from "dayjs";
import { apiUrl, authHeaders, SAMPLE } from "./config";
import { RightModal, RSelect, RDatePicker, useNotify, AiAssistBtn, AI_ENABLED, SingleDatePicker, EngagementDatePicker } from "./shared";
import SendTestModal from "./SendTestModal";
import AudienceBuilder, { emptyFilters, applyFilters, profileCompleteness, PROFILE_FIELDS } from "./AudienceBuilder";
import CampaignScheduler from "./CampaignScheduler";

const CHANNEL_COLOR = { WhatsApp: "#198754", SMS: "#6c757d", Email: "#0d6efd" };

// ─── Channel Fallback Builder ──────────────────────────────────────────────────
// Lets admin set priority: try WhatsApp first, fallback to SMS, then Email
const ChannelFallback = ({ fallback, setFallback }) => {
  const CHANNELS = ["WhatsApp", "SMS", "Email"];

  const toggleChannel = (ch) =>
    setFallback((p) => ({
      ...p,
      channels: p.channels.includes(ch)
        ? p.channels.filter((c) => c !== ch)
        : [...p.channels, ch],
    }));

  const moveUp = (idx) => {
    if (idx === 0) return;
    const arr = [...fallback.channels];
    [arr[idx-1], arr[idx]] = [arr[idx], arr[idx-1]];
    setFallback((p) => ({ ...p, channels: arr }));
  };

  const moveDown = (idx) => {
    if (idx === fallback.channels.length - 1) return;
    const arr = [...fallback.channels];
    [arr[idx], arr[idx+1]] = [arr[idx+1], arr[idx]];
    setFallback((p) => ({ ...p, channels: arr }));
  };

  return (
    <Card className="shadow-sm border-0">
      <Card.Header className="bg-white border-bottom py-3">
        <h6 className="fw-bold mb-0">
          <i className="fas fa-layer-group text-primary me-2"></i>
          Channel & Fallback
        </h6>
      </Card.Header>
      <Card.Body className="p-4">
        <div className="alert alert-info py-2 mb-3" style={{ fontSize: "0.82rem" }}>
          <i className="fas fa-info-circle me-2"></i>
          Set the order in which channels are tried. If a guest has no WhatsApp number, the system
          falls back to the next channel automatically.
        </div>

        <Form.Label className="fw-semibold small">Selected channels (drag to reorder priority)</Form.Label>
        <div className="mb-3">
          {fallback.channels.map((ch, idx) => (
            <div key={ch}
              className="d-flex align-items-center gap-3 p-2 rounded border mb-2"
              style={{ background: "#f9fafb" }}>
              <div className="fw-bold text-muted" style={{ minWidth: 20, fontSize: "0.78rem" }}>
                {idx + 1}
              </div>
              <div className="d-flex align-items-center gap-2 flex-grow-1">
                {ch === "WhatsApp" && <FaWhatsapp className="text-success" />}
                {ch === "SMS"      && <FaSms className="text-secondary" />}
                {ch === "Email"    && <FaEnvelope className="text-primary" />}
                <span className="fw-semibold" style={{ fontSize: "0.88rem" }}>{ch}</span>
                {idx === 0 && <Badge bg="success" style={{ fontSize: "0.65rem" }}>Primary</Badge>}
                {idx > 0   && <Badge bg="secondary" style={{ fontSize: "0.65rem" }}>Fallback {idx}</Badge>}
              </div>
              <div className="d-flex gap-1">
                <Button variant="light" size="sm" className="py-0 px-2" onClick={() => moveUp(idx)}
                  disabled={idx === 0} title="Move up (higher priority)">
                  <i className="fas fa-chevron-up" style={{ fontSize: "0.7rem" }}></i>
                </Button>
                <Button variant="light" size="sm" className="py-0 px-2" onClick={() => moveDown(idx)}
                  disabled={idx === fallback.channels.length - 1} title="Move down">
                  <i className="fas fa-chevron-down" style={{ fontSize: "0.7rem" }}></i>
                </Button>
                <Button variant="light" size="sm" className="py-0 px-2 text-danger"
                  onClick={() => toggleChannel(ch)} title="Remove channel">
                  ×
                </Button>
              </div>
            </div>
          ))}
        </div>

        <Form.Label className="fw-semibold small">Add channel</Form.Label>
        <div className="d-flex gap-2 mb-3">
          {CHANNELS.filter((ch) => !fallback.channels.includes(ch)).map((ch) => (
            <Button key={ch} size="sm" variant="outline-secondary" className="rounded-pill"
              onClick={() => toggleChannel(ch)}>
              {ch === "WhatsApp" && <FaWhatsapp className="me-1 text-success" />}
              {ch === "SMS"      && <FaSms className="me-1 text-secondary" />}
              {ch === "Email"    && <FaEnvelope className="me-1 text-primary" />}
              + {ch}
            </Button>
          ))}
          {CHANNELS.every((ch) => fallback.channels.includes(ch)) && (
            <span className="text-muted small">All channels selected</span>
          )}
        </div>

        {/* Fallback logic explanation */}
        {fallback.channels.length > 1 && (
          <div className="p-3 rounded" style={{ background: "#f0f4ff", border: "1px solid #c7d2fe", fontSize: "0.78rem" }}>
            <strong>Delivery logic:</strong>
            <div className="d-flex align-items-center flex-wrap gap-1 mt-1">
              {fallback.channels.map((ch, idx) => (
                <React.Fragment key={ch}>
                  <span className="px-2 py-1 rounded text-white fw-semibold"
                    style={{ background: CHANNEL_COLOR[ch], fontSize: "0.74rem" }}>
                    {ch === "WhatsApp" && <FaWhatsapp className="me-1" />}
                    {ch === "SMS"      && <FaSms className="me-1" />}
                    {ch === "Email"    && <FaEnvelope className="me-1" />}
                    {ch}
                  </span>
                  {idx < fallback.channels.length - 1 && (
                    <>
                      <FaArrowRight className="text-muted" size={10} />
                      <span className="text-muted" style={{ fontSize: "0.7rem" }}>
                        if no {ch}
                      </span>
                      <FaArrowRight className="text-muted" size={10} />
                    </>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

// ─── Landing page section preview (used inline in campaign form) ───────────────
const LandingPageSectionPreview = ({ section }) => {
  const s = { fontSize: "0.8rem", color: "#374151" };
  switch (section.type) {
    case "banner":
      return section.imageUrl ? (
        <div style={{ borderRadius: 8, overflow: "hidden", height: 80 }}>
          <img src={section.imageUrl} alt={section.alt || "Banner"}
            style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        </div>
      ) : (
        <div style={{ height: 60, borderRadius: 8, background: "#f1f5f9",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: "0.72rem", color: "#94a3b8" }}>
          <i className="fas fa-image me-1" /> Banner image
        </div>
      );
    case "title":
      return (
        <div style={{ fontWeight: 800, fontSize: "1rem", color: section.color || "#111827",
          textAlign: section.align || "center" }}>
          {section.text || "Untitled"}
        </div>
      );
    case "description":
      return (
        <div style={{ ...s, color: section.color || "#6b7280", textAlign: section.align || "left",
          lineHeight: 1.5 }}>
          {section.text || ""}
        </div>
      );
    case "price":
      return (
        <div className="d-flex align-items-center gap-2">
          <span style={{ fontWeight: 800, fontSize: "1.1rem", color: "#111827" }}>
            {section.amount || "₹0"}
          </span>
          {section.originalAmount && (
            <span style={{ fontSize: "0.82rem", color: "#94a3b8", textDecoration: "line-through" }}>
              {section.originalAmount}
            </span>
          )}
          {section.badge && (
            <span style={{ fontSize: "0.65rem", fontWeight: 700, padding: "2px 7px",
              borderRadius: 20, background: "#dcfce7", color: "#15803d" }}>
              {section.badge}
            </span>
          )}
        </div>
      );
    case "cta":
      return (
        <div style={{ textAlign: "center" }}>
          <span style={{ display: "inline-block", padding: "7px 22px", borderRadius: 8,
            background: section.color || "#5b5bd6", color: "#fff",
            fontWeight: 700, fontSize: "0.82rem" }}>
            {section.text || "Book Now"}
          </span>
        </div>
      );
    default:
      return (
        <div style={{ ...s, color: "#94a3b8", fontStyle: "italic" }}>
          [{section.type}] section
        </div>
      );
  }
};

// ─── Message preview ───────────────────────────────────────────────────────────
const MessagePreview = ({ channel, content, landingPage }) => {
  const linkText = landingPage ? `hotel.co/o/${landingPage.slug}` : "hotel.co/link";
  const msg = (content || "")
    .replace("{{guest_name}}",   "Rahul Sharma")
    .replace("{{hotel_name}}",   "Grand Hotel")
    .replace("{{link}}",         linkText);

  if (channel === "WhatsApp") return (
    <div className="p-3 rounded" style={{ background: "#e5ddd5" }}>
      {!content
        ? <div className="text-center text-muted py-2 small">Select template to preview</div>
        : <div className="ms-auto p-3" style={{
            background: "#dcf8c6", maxWidth: "92%",
            fontSize: "0.84rem", lineHeight: 1.6,
            borderRadius: "12px 0 12px 12px", whiteSpace: "pre-wrap",
          }}>
            {msg}
            <div className="text-end text-muted mt-1" style={{ fontSize: "0.68rem" }}>✓✓</div>
          </div>}
    </div>
  );
  return (
    <div className="border rounded p-3"
      style={{ background: "#f8f9fa", fontSize: "0.84rem", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
      {msg || <span className="text-muted fst-italic">Select template to preview...</span>}
    </div>
  );
};

// ─── Profile completeness summary ─────────────────────────────────────────────
const ProfileCompleteSummary = ({ guests }) => {
  if (!guests.length) return null;
  const breakdown = PROFILE_FIELDS.map((f) => ({
    ...f,
    missingCount: guests.filter((g) => f.check(g)).length,
  }));
  return (
    <div className="mt-3 p-3 rounded border" style={{ background: "#fffbeb", borderColor: "#fde68a" }}>
      <div className="fw-semibold small mb-2">
        <FaUserEdit className="text-warning me-1" />Profile gaps in your audience:
      </div>
      {breakdown.map((f) => (
        f.missingCount > 0 && (
          <div key={f.key} className="d-flex justify-content-between mb-1" style={{ fontSize: "0.78rem" }}>
            <span>{f.icon} {f.label}</span>
            <span className="fw-bold text-danger">{f.missingCount} missing</span>
          </div>
        )
      ))}
      <div className="text-muted mt-2" style={{ fontSize: "0.72rem" }}>
        These guests will receive the profile completion message with a unique link.
      </div>
    </div>
  );
};

// ─── Main CampaignForm ─────────────────────────────────────────────────────────
const CampaignForm = ({ editingCampaign, onNavigate }) => {
  // DB returns event_date, campaign_type, steps (JSONB array) — normalise all
  const ec = editingCampaign;
  const [name,          setName]          = useState(ec?.name || "");
  const [eventType,     setEventType]     = useState(ec?.campaign_type || ec?.eventType || "one-time");
  const [eventDate,     setEventDate]     = useState(ec?.event_date || ec?.eventDate || "");
  const [filters,       setFilters]       = useState({ ...emptyFilters(), ...(ec?.filters || {}) });
  const [steps,         setSteps]         = useState(
    (ec?.steps || ec?.steps_data || []).length
      ? (ec?.steps || ec?.steps_data).map(s => ({
          ...s,
          templateId:    String(s.templateId    || s.template_id    || ""),
          landingPageId: String(s.landingPageId || s.landing_page_id || ""),
          sendTime:      s.sendTime || s.executionTime || "09:00",
        }))
      : [{ id:"cs_1", offsetDays:0, sendTime:"09:00", channel:"WhatsApp", templateId:"", landingPageId:"", note:"" }]
  );
  const [fallback,      setFallback]      = useState(ec?.fallback || { channels: [] });
  const [revenueGoal,   setRevenueGoal]   = useState(ec?.revenue_goal || ec?.revenueGoal || "");

  const [allGuests,     setAllGuests]     = useState([]);
  const [templates,     setTemplates]     = useState([]);
  const [landingPages,  setLandingPages]  = useState([]);
  const [loadingGuests, setLoadingGuests] = useState(true);
  const [saving,        setSaving]        = useState(false);
  const [error,         setError]         = useState("");
  const [success,       setSuccess]       = useState("");
  const [showTest,      setShowTest]      = useState(false);
  const notify = useNotify();

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [gRes, tRes, lpRes] = await Promise.all([
        axios.get(`${apiUrl}/feedback/guests`,        authHeaders()),
        axios.get(`${apiUrl}/feedback/templates`,     authHeaders()),
        axios.get(`${apiUrl}/feedback/landing-pages`, authHeaders()),
      ]);
      const guests = Array.isArray(gRes.data) ? gRes.data : (gRes.data?.guests || []);
      setAllGuests(guests.map((g) => ({ ...g, id: g.guestid || g.id })));
      setTemplates((tRes.data || []).map((t) => ({
        ...t,
        id:            String(t.templateid || t.id),
        linkType:      t.link_type      || t.linkType      || "none",
        landingPageId: String(t.landing_page_id || t.landingPageId || ""),
        surveyId:      String(t.survey_id      || t.surveyId      || ""),
      })));
      setLandingPages((lpRes.data || []).map((p) => ({ ...p, id: String(p.pageid || p.id) })));
    } catch {
      setAllGuests([]);
      setTemplates([]);
      setLandingPages([]);
    } finally {
      setLoadingGuests(false);
    }
  };

  const matchedGuests     = applyFilters(allGuests, filters);
  const totalCredits      = matchedGuests.length * steps.length;
  const isProfileCampaign = filters.onlyIncomplete || filters.missingFields.length > 0;

  const save = async () => {
    if (!name.trim()) { setError("Campaign name is required."); return; }
    setSaving(true);
    setError("");
    const payload = {
      name, eventType, eventDate, filters, steps, fallback,
      revenueGoal: Number(revenueGoal) || 0,
      audienceCount: matchedGuests.length,
      totalCredits,
      status: "Scheduled",
    };
    const id = editingCampaign?.campaignid || editingCampaign?.id;
    try {
      if (id) {
        await axios.put(`${apiUrl}/feedback/campaigns/${id}`, payload, authHeaders());
      } else {
        await axios.post(`${apiUrl}/feedback/campaigns`, payload, authHeaders());
      }
      setSuccess("Campaign saved and scheduled!");
      notify("success", "Campaign saved and scheduled!");
    } catch {
      setSuccess("Campaign saved (demo mode).");
      notify("info", "Campaign saved (demo mode).");
    } finally {
      setSaving(false);
      setTimeout(() => onNavigate("campaigns"), 1400);
    }
  };

  const EVENT_TYPES = [
    { value: "one-time",    label: "One-time" },
    { value: "anniversary", label: "Anniversary" },
    { value: "birthday",    label: "Birthday" },
    { value: "checkin",     label: "Check-in" },
    { value: "checkout",    label: "Check-out" },
  ];

  return (
    <div>
      {/* Header */}
      <div className="page-header mb-4">
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-3">
            <button onClick={() => onNavigate("campaigns")}
              style={{ width:34, height:34, borderRadius:8, border:"1px solid #e5e7eb",
                background:"#fff", cursor:"pointer", display:"flex",
                alignItems:"center", justifyContent:"center", color:"#6b7280",
                flexShrink:0 }}
              title="Back to campaigns">
              <FaArrowLeft size={13} />
            </button>
            <div>
              <h3 className="fw-bold text-dark mb-0">
                <i className="fas fa-bullhorn text-primary me-2"></i>
                {editingCampaign ? "Edit Campaign" : "Create Campaign"}
              </h3>
              <small className="text-muted">
                Define audience · set schedule · choose templates
              </small>
            </div>
          </div>
          <div className="d-flex gap-2">
            <Button variant="outline-secondary" onClick={() => setShowTest(true)} className="px-3">
              <FaPaperPlane className="me-1" />Send Test
            </Button>
            <Button variant="primary" onClick={save} disabled={saving} className="px-4">
              {saving
                ? <><Spinner size="sm" animation="border" className="me-1" />Saving...</>
                : <><FaRocket className="me-1" />{matchedGuests.length > 0 ? `Launch to ${matchedGuests.length} guests` : "Save Campaign"}</>}
            </Button>
          </div>
        </div>
      </div>

      {error   && <Alert variant="danger"  dismissible onClose={() => setError("")}>{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      <Row className="g-3">
        {/* LEFT: Form */}
        <Col lg={8}>

          {/* Step 1: Details */}
          <Card className="shadow-sm border-0 mb-3">
            <Card.Header className="bg-white border-bottom py-3">
              <h6 className="fw-bold mb-0"><span className="badge bg-primary me-2">1</span>Campaign Details</h6>
            </Card.Header>
            <Card.Body className="p-4">
              <Row className="g-3">
                <Col md={6}>
                  <Form.Label className="fw-semibold">Campaign Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control value={name} onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Profile Completion — 10% Off" aria-required="true" />
                </Col>
                <Col md={6}>
                  <Form.Label className="fw-semibold">Event Type</Form.Label>
                  <Form.Select value={eventType} onChange={(e) => setEventType(e.target.value)}>
                    {EVENT_TYPES.map((et) => <option key={et.value} value={et.value}>{et.label}</option>)}
                  </Form.Select>
                </Col>
                {eventType === "one-time" && (
                  <Col md={6}>
                    <Form.Label className="fw-semibold">
                      <FaCalendarAlt className="me-1 text-primary" />Target Date
                    </Form.Label>
                    <SingleDatePicker
                      value={eventDate}
                      onChange={setEventDate}
                      placeholder="Select campaign date"
                    />
                    <Form.Text className="text-muted" style={{ marginTop:4, display:"block" }}>
                      Steps fire relative to this date.
                    </Form.Text>
                  </Col>
                )}
              </Row>
            </Card.Body>
          </Card>

          {/* Step 2: Audience */}
          <div className="mb-3">
            <div className="d-flex align-items-center mb-2">
              <span className="badge bg-info me-2">2</span>
              <h6 className="fw-bold mb-0">Audience</h6>
            </div>
            <AudienceBuilder
              filters={filters}
              setFilters={setFilters}
              matchedCount={matchedGuests.length}
              totalCount={allGuests.length}
              loading={loadingGuests}
            />
            {isProfileCampaign && <ProfileCompleteSummary guests={matchedGuests} />}
          </div>

          {/* Step 3: Schedule — template + landing page per step */}
          <div className="mb-3">
            <div className="d-flex align-items-center mb-2">
              <span className="badge bg-success me-2">3</span>
              <h6 className="fw-bold mb-0">Schedule &amp; Messages</h6>
            </div>
            <CampaignScheduler
              steps={steps}
              setSteps={setSteps}
              templates={templates}
              landingPages={landingPages}
              eventDate={eventDate}
              eventLabel={EVENT_TYPES.find((e) => e.value === eventType)?.label || "Event"}
            />
          </div>

          {/* Step 4: Channel & Fallback */}
          <div className="mb-3">
            <div className="d-flex align-items-center mb-2">
              <span className="badge bg-warning me-2">4</span>
              <h6 className="fw-bold mb-0">Channel & Fallback</h6>
            </div>
            <ChannelFallback fallback={fallback} setFallback={setFallback} />
          </div>

          {/* Step 5: ROI Goal */}
          <Card className="shadow-sm border-0">
            <Card.Header className="bg-white border-bottom py-3">
              <h6 className="fw-bold mb-0"><span className="badge bg-secondary me-2">5</span>ROI Target</h6>
            </Card.Header>
            <Card.Body className="p-4">
              <Row className="g-3 align-items-end">
                <Col md={5}>
                  <Form.Label className="fw-semibold">
                    <FaRupeeSign className="me-1 text-success" />Revenue Goal (₹)
                  </Form.Label>
                  <Form.Control type="number" min="0" value={revenueGoal}
                    onChange={(e) => setRevenueGoal(e.target.value)} placeholder="e.g. 50000" />
                </Col>
                <Col md={4}>
                  <div className="p-3 rounded text-center" style={{ background: "#f0fdf4", border: "1px solid #bbf7d0" }}>
                    <div className="text-muted small">Est. credits</div>
                    <div className="fw-bold text-success h4 mb-0">{totalCredits.toLocaleString()}</div>
                    <div className="text-muted" style={{ fontSize: "0.7rem" }}>
                      {matchedGuests.length} guests × {steps.length} steps × {fallback.channels.length} channels
                    </div>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>

        {/* RIGHT: Summary Panel */}
        <Col lg={4}>
          <Card className="shadow-sm border-0" style={{ position: "sticky", top: 80 }}>
            <Card.Header className="bg-white border-bottom py-3">
              <h6 className="fw-bold mb-0"><FaEye className="text-primary me-2" />Summary</h6>
            </Card.Header>
            <Card.Body className="p-4">

              {/* Audience */}
              <div className="mb-3 p-3 rounded" style={{ background: "#f0f4ff", border: "1px solid #c7d2fe" }}>
                <div className="d-flex justify-content-between align-items-center">
                  <span className="fw-semibold small"><FaUsers className="me-1 text-primary" />Audience</span>
                  <span className="fw-bold text-primary h5 mb-0">{matchedGuests.length}</span>
                </div>
                <div className="text-muted" style={{ fontSize: "0.72rem" }}>guests match filters</div>
                {isProfileCampaign && matchedGuests.length > 0 && (
                  <div className="mt-1 text-warning fw-semibold" style={{ fontSize: "0.72rem" }}>
                    <FaUserEdit className="me-1" />Profile completion campaign
                  </div>
                )}
                {matchedGuests.length > 0 && (
                  <div className="mt-1" style={{ fontSize: "0.72rem", color: "#4338ca" }}>
                    {matchedGuests.slice(0,3).map((g) => g.name).join(", ")}
                    {matchedGuests.length > 3 && ` +${matchedGuests.length-3} more`}
                  </div>
                )}
              </div>

              {/* Channel fallback */}
              <div className="mb-3">
                <div className="fw-semibold small mb-2">Channel order</div>
                <div className="d-flex align-items-center gap-1 flex-wrap" style={{ fontSize: "0.78rem" }}>
                  {fallback.channels.map((ch, idx) => (
                    <React.Fragment key={ch}>
                      <span className="px-2 py-1 rounded text-white"
                        style={{ background: CHANNEL_COLOR[ch], fontSize: "0.72rem" }}>
                        {ch === "WhatsApp" && <FaWhatsapp className="me-1" />}
                        {ch === "SMS"      && <FaSms className="me-1" />}
                        {ch === "Email"    && <FaEnvelope className="me-1" />}
                        {ch}
                      </span>
                      {idx < fallback.channels.length - 1 && (
                        <FaArrowRight className="text-muted" size={10} />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>

              {/* Schedule summary */}
              <div className="mb-3">
                <div className="fw-semibold small mb-2">Schedule</div>
                {steps.map((step, i) => {
                  const tpl = templates.find(t => t.id === step.templateId);
                  return (
                  <div key={step.id} className="d-flex align-items-center gap-2 mb-1"
                    style={{ fontSize: "0.78rem" }}>
                    <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 text-white fw-bold"
                      style={{ width: 20, height: 20, background: CHANNEL_COLOR[step.channel] || "#6c757d", fontSize: "0.62rem" }}>
                      {i+1}
                    </div>
                    <span className="text-muted">
                      {step.offsetDays === 0 ? "Same day" : step.offsetDays < 0 ? `${Math.abs(step.offsetDays)}d before` : `${step.offsetDays}d after`}
                      {" at "}{step.sendTime}
                      {tpl && <span className="text-dark ms-1">· {tpl.name}</span>}
                    </span>
                  </div>
                  );
                })}
              </div>

              {/* Credits */}
              <div className="p-2 rounded border mb-3" style={{ background: "#fffbeb", borderColor: "#fde68a", fontSize: "0.78rem" }}>
                <i className="fas fa-coins text-warning me-1"></i>
                Est. credits: <strong>{totalCredits.toLocaleString()}</strong>
                <span className="text-muted ms-1">({matchedGuests.length} guests × {steps.length} steps)</span>
              </div>

              <Button variant="primary" className="w-100 fw-semibold" onClick={save}
                disabled={saving}>
                {saving
                  ? <><Spinner size="sm" animation="border" className="me-1" />Saving...</>
                  : matchedGuests.length > 0
                    ? <><FaRocket className="me-1" />Launch to {matchedGuests.length} guests</>
                    : <><FaRocket className="me-1" />Save Campaign</>}
              </Button>
              {matchedGuests.length === 0 && (
                <div className="text-center text-muted mt-2" style={{ fontSize: "0.75rem" }}>
                  No guests match filters yet — you can still save and launch later
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <SendTestModal
        show={showTest}
        onHide={() => setShowTest(false)}
        campaignName={name}
        templateContent={templates.find((t) => t.id === steps[0]?.templateId)?.content}
        templateChannel={steps[0]?.channel}
        steps={steps}
      />

      <style>{`
        .card { border-radius: 12px !important; }
        .card-header { border-radius: 12px 12px 0 0 !important; }
        .page-header { margin-bottom: 1.5rem; }
      `}</style>
    </div>
  );
};

export default CampaignForm;
